import React, { useEffect, useState } from 'react';

import { Box } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useField } from 'formik';
import { Autocomplete, AutocompleteProps } from 'formik-mui';
import { useSnackbar } from 'notistack';
import { useDebounce } from 'use-debounce';

import { CissTextField } from 'components/fields/';
import { RequestListagemProps, RequestOptionsType, useRequestListagem } from 'hooks/useRequestListagem';

interface ComunicadoTagOptionsProps {
    idTagComunicado: number;
    dsTagComunicado: string;
}

interface ComunicadoTagFieldProps
    extends AutocompleteProps<ComunicadoTagOptionsProps, boolean | undefined, boolean | undefined, boolean | undefined> {
    label?: string;
}

interface RequestListagemType extends RequestListagemProps {
    data: {
        data: ComunicadoTagOptionsProps[];
    };
}

export function ComunicadoTagFieldFormik({ onChange, multiple = true, label = 'Tags', ...PropsFormik }: ComunicadoTagFieldProps) {
    const [open, setOpen] = useState<boolean>(false);
    const [inputValue, setInputValue] = useState<string>('');
    const [query, setQuery] = useState('');
    const { enqueueSnackbar } = useSnackbar();
    const RequestListagem = useRequestListagem();
    const [field, { error }] = useField(PropsFormik.field.name);
    const [queryValue] = useDebounce(query, 500);

    const requestOptions: RequestOptionsType = {
        url: '/gestao/tagcomunicado',
        query: queryValue,
        sort: [{ property: 'idTagComunicado', direction: 'ASC' }],
        columns: 'idTagComunicado,dsTagComunicado',
        limit: 50,
    };

    const { isLoading, fetchStatus, data } = useQuery(
        [requestOptions],
        () => {
            const request: Promise<RequestListagemType> = RequestListagem(requestOptions);
            return request;
        },
        {
            keepPreviousData: false,
            enabled: open,
            onError: () => {
                enqueueSnackbar('Falha ao processar solicitação de tags', { variant: 'error' });
            },
        },
    );
    useEffect(() => {
        if (open) {
            setQuery(inputValue);
        }
    }, [inputValue, open]);

    return (
        <Autocomplete
            {...PropsFormik}
            multiple={multiple}
            onChange={onChange}
            loading={isLoading && fetchStatus === 'fetching'}
            options={data?.data?.data ?? []}
            isOptionEqualToValue={(option, value) => option.idTagComunicado === value.idTagComunicado}
            getOptionLabel={(option: ComunicadoTagOptionsProps | string) => {
                if (typeof option === 'string') {
                    return option;
                }
                return option.dsTagComunicado;
            }}
            renderInput={(params) => <CissTextField {...params} label={label} error={Boolean(error)} helperText={error} />}
            onInputChange={(event, newInputValue) => {
                setInputValue(newInputValue);
            }}
            onOpen={() => {
                setOpen(true);
            }}
            onClose={() => {
                setOpen(false);
            }}
            renderOption={(props, option) => (
                <Box component="li" {...props} key={option.idTagComunicado}>
                    {option.idTagComunicado} - {option.dsTagComunicado}
                </Box>
            )}
            limitTags={1}
        />
    );
}
